import { Col, Row } from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';
import CollapsablePreview from '../../../../shared/components/collapsable-preview/collapsable-preview';
import PdfViewer from '../../../../shared/components/pdf-viewer/pdf-viewer';
import { VectorIcon } from '../../../../shared/icons/vector-icon';
import { convertServerDateTimeString } from '../../../../shared/utils/format-date/format-date';
import RequestPreviewContentData from '../../../request/components/request-preview-content-data/request-preview-content-data';
import { ArchiveFile } from '../../models/archive-file';
import { getArchiveFileUrl } from '../../utils/get-archive-file-url';
import styles from './archive-file-upload-preview.module.css';
import { useGetUserSettingsQuery } from '../../../user/api/user-api';
import { UserSettings } from '../../../user/models/user-settings';

interface Props {
  files: Omit<ArchiveFile, 'expiresAt' | 'request'>[];
}

const ArchiveFileUploadPreview = ({ files }: Props): JSX.Element => {
  const { t } = useTranslation(['archive', 'common']);
  const { data: userSettings } = useGetUserSettingsQuery();

  return (
    <>
      {files.map(file => (
        <CollapsablePreview
          title={`${file.name || t('filePreview.label.file')}`}
          index={file.id}
          key={file.id}
          icon={<VectorIcon />}
        >
          <>
            <RequestPreviewContentData
              contentData={[
                {
                  title: t('filePreview.label.size'),
                  data: `${file.size} B`,
                },
                {
                  title: t('filePreview.label.fileType'),
                  data: file.mimeType,
                },
                {
                  title: t('filePreview.label.createdAt'),
                  data: convertServerDateTimeString(file.createdAt, userSettings?.[UserSettings.DateFormat]),
                },
              ]}
            />
            <Row className={styles.actionButtonRow} justify="start" gutter={[10, 10]}>
              <Col>
                <a
                  href={getArchiveFileUrl(file.id, true)}
                  className={clsx({ [styles.downloadActionDisabled]: file.deleted })}
                  download
                >
                  <CustomButton size="small" type="primaryLight" fontWeight={400} fontSize={12} disabled={file.deleted}>
                    {t('action.download', { ns: 'common' })}
                  </CustomButton>
                </a>
              </Col>
            </Row>
            {!file.deleted && (
              <div className={styles.fileWrapper}>
                {file.mimeType === 'application/pdf' ? (
                  <PdfViewer pdfSource={getArchiveFileUrl(file.id)} />
                ) : (
                  file.mimeType.startsWith('image/') && (
                    <img src={getArchiveFileUrl(file.id)} alt={file.name} className={styles.fileImage} />
                  )
                )}
              </div>
            )}
          </>
        </CollapsablePreview>
      ))}
    </>
  );
};

export default ArchiveFileUploadPreview;
